import React, { useState, useContext, useRef, useCallback, useEffect } from "react";
import { X, Phone, Flame, MapPin, AlertTriangle, Loader2, Plus } from "lucide-react";
import NumberFormat from "react-number-format";
import { LanguageContext, CenterContext } from "../../../../../Context";
import mut from "../../../hooks/mut";
import { net } from "../../../../../database";
import useCTX from "../../../hooks/useCTX";


const HEAT_LEVELS = [
  { value: 1, label: "1", color: "#ef4444", labelKey: "marketing.heat1" },
  { value: 2, label: "2", color: "#f97316", labelKey: "marketing.heat2" },
  { value: 3, label: "3", color: "#eab308", labelKey: "marketing.heat3" },
  { value: 4, label: "4", color: "#22c55e", labelKey: "marketing.heat4" },
  { value: 5, label: "5", color: "#10b981", labelKey: "marketing.heat5" },
];

export default function MarketingAddContactModal({ open, onClose, onSaved }) {
  const { l, language } = useContext(LanguageContext);
  const { center } = useContext(CenterContext);
  const {user} = useCTX()

  const [form, setForm] = useState({
    phone: "",
    name: "",
    notes: "",
    heat: 3,
    location: "",
    customLocation: "",
  });
  const [saving, setSaving] = useState(false);
  const [phoneCheck, setPhoneCheck] = useState({
    checking: false,
    duplicates: [],
    acknowledged: false,
  });
  const [showCustomLocation, setShowCustomLocation] = useState(false);
  const [addingLocation, setAddingLocation] = useState(false);
  const debounceRef = useRef(null);
  const phoneInputRef = useRef(null);
  const hasAutoFocused = useRef(false);

  const t = useCallback(
    (key, fallback) => {
      try {
        return l[key]?.[language] || fallback || key;
      } catch {
        return fallback || key;
      }
    },
    [l, language]
  );

  const marketingLocations = center?.marketingLocations || [];

  useEffect(() => {
    if (open && !hasAutoFocused.current) {
      hasAutoFocused.current = true;
      setTimeout(() => phoneInputRef.current?.focus(), 50);
    }
    if (!open) {
      hasAutoFocused.current = false;
    }
  }, [open]);

  useEffect(() => {
    if (!open) {
      setForm({ phone: "", name: "", notes: "", heat: 3, location: "", customLocation: "" });
      setPhoneCheck({ checking: false, duplicates: [], acknowledged: false });
      setShowCustomLocation(false);
      setAddingLocation(false);
    }
  }, [open]);

  const checkDuplicatePhone = useCallback(
    async (phone) => {
      if (!phone.trim()) {
        setPhoneCheck((prev) => ({ ...prev, checking: false, duplicates: [] }));
        return;
      }
      setPhoneCheck((prev) => ({ ...prev, checking: true }));
      try {
      
        const result = await net.get(
          `/api/pipeline/check-phone?phone=${encodeURIComponent(phone.trim())}`,
        );
   
        setPhoneCheck({ checking: false, duplicates: result.leads || [], acknowledged: false });
      } catch {
        setPhoneCheck({ checking: false, duplicates: [], acknowledged: false });
      }
    },
    []
  );

  const handlePhoneValueChange = useCallback(
    (values) => {
      const raw = values.value;
      setForm((prev) => ({ ...prev, phone: raw }));
      setPhoneCheck((prev) => ({ ...prev, acknowledged: false }));
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (raw.length >= 8) {
        debounceRef.current = setTimeout(() => checkDuplicatePhone(raw), 400);
      } else {
        setPhoneCheck({ checking: false, duplicates: [], acknowledged: false });
      }
    },
    [checkDuplicatePhone]
  );

  const addNewLocation = async () => {
    const newLoc = form.customLocation.trim();
    if (!newLoc) return;
    setAddingLocation(true);
    try {
      const result = await mut("post", "/center", {
        marketingLocations: [...marketingLocations, newLoc],
      });
      if (result?.marketingLocations) {
        setForm((prev) => ({ ...prev, location: newLoc, customLocation: "" }));
        setShowCustomLocation(false);
      }
    } finally {
      setAddingLocation(false);
    }
  };

  if (!open) return null;

  const hasDuplicates =
    phoneCheck.duplicates.length > 0 && !phoneCheck.acknowledged;
  const canSubmit =
    form.phone.trim().length > 0 && !phoneCheck.checking && !hasDuplicates;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.phone.trim()) return;
    setSaving(true);
    try {
      const payload = {
        phone: form.phone,
        name: form.name,
        notes: form.notes,
        heat: form.heat,
        location: form.location,
        marketingPerson: user._id
      };
      const result = await mut("post", "/add-marketing-number", payload);
      if (result && onSaved) {
        onSaved(result);
      }
      onClose();
    } finally {
      setSaving(false);
    }
  };

  const handleBackdrop = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  const inputClass =
    `w-full px-3 py-2 rounded-[10px] border border-slate-200 text-sm text-slate-800 placeholder:text-slate-300 focus:outline-none focus:border-teal-800 focus:ring-1 focus:ring-teal-800/20 transition-colors duration-150`;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ backgroundColor: "rgba(15, 23, 42, 0.5)", backdropFilter: "blur(2px)" }}
      onClick={handleBackdrop}
    >
      <div className="bg-white rounded-[12px] shadow-[0_8px_30px_rgba(0,0,0,0.12)] w-full max-w-md overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <div>
            <h2 className="font-heading text-lg font-semibold text-slate-800">
              {t("marketingNewContact", "New Contact")}
            </h2>
            <p className="text-xs text-slate-500 mt-0.5">
              {t("marketingAddContactDesc", "Add a new marketing contact")}
            </p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors duration-150 cursor-pointer"
          >
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
          <div className="px-6 py-5 space-y-5">
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
                {t("pipeline.phoneRequired", "Phone *")}
              </label>
              <div className="relative">
                <Phone
                  size={15}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
                />
                <NumberFormat
                  format="###-###-####"
                  mask="_"
                  value={form.phone}
                  onValueChange={handlePhoneValueChange}
                  getInputRef={phoneInputRef}
                  className={`${inputClass} pl-9 pr-3 py-2.5`}
                  placeholder="___-___-____"
                />
                {phoneCheck.checking && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    <div className="w-4 h-4 border-2 border-slate-200 border-t-teal-600 rounded-full animate-spin" />
                  </div>
                )}
              </div>
              {hasDuplicates && (
                <div className="mt-2 p-3 rounded-[10px] bg-amber-50 border border-amber-200">
                  <div className="flex items-start gap-2">
                    <AlertTriangle
                      size={15}
                      className="text-amber-500 mt-0.5 flex-shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold text-amber-800 mb-1">
                        {t(
                          "marketingDuplicatePhone",
                          "This phone number already exists"
                        )}
                      </p>
                      <button
                        type="button"
                        onClick={() =>
                          setPhoneCheck((prev) => ({
                            ...prev,
                            acknowledged: true,
                          }))
                        }
                        className="text-xs font-semibold text-amber-700 hover:text-amber-900 underline underline-offset-2 transition-colors duration-150 cursor-pointer"
                      >
                        {t("pipeline.createAnyway", "Create anyway")}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
                {t("pipeline.name", "Name")}
              </label>
              <input
                type="text"
                value={form.name}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, name: e.target.value }))
                }
                className={inputClass}
                placeholder={t(
                  "marketing.customerName",
                  "Customer name (optional)"
                )}
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
                {t("marketing.heat", "Heat")}
              </label>
              <div className="flex gap-2">
                {HEAT_LEVELS.map((level) => (
                  <button
                    key={level.value}
                    type="button"
                    onClick={() =>
                      setForm((prev) => ({ ...prev, heat: level.value }))
                    }
                    className={`flex-1 flex flex-col items-center gap-1 p-2.5 rounded-[10px] border text-xs font-semibold transition-all duration-150 cursor-pointer ${
                      form.heat === level.value
                        ? "shadow-sm"
                        : "border-slate-200 text-slate-500 hover:bg-slate-50"
                    }`}
                    style={
                      form.heat === level.value
                        ? {
                            borderColor: level.color,
                            backgroundColor: `${level.color}10`,
                            color: level.color,
                          }
                        : {}
                    }
                  >
                    <Flame size={14} style={{ color: form.heat === level.value ? level.color : undefined }} />
                    <span>{level.value}</span>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
                <MapPin size={12} className="inline mr-1" />
                {t("marketing.location", "Location")}
              </label>
              {!showCustomLocation ? (
                <div className="space-y-1.5">
                  <select
                    value={form.location}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, location: e.target.value }))
                    }
                    className={`${inputClass} bg-white`}
                  >
                    <option value="">
                      {t("marketing.selectLocation", "Select location")}
                    </option>
                    {marketingLocations.map((loc) => (
                      <option key={loc} value={loc}>
                        {loc}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={() => setShowCustomLocation(true)}
                    className="text-xs font-semibold text-teal-600 hover:text-teal-800 flex items-center gap-1 cursor-pointer"
                  >
                    <Plus size={11} />
                    {t("marketing.addNewLocation", "Add new location")}
                  </button>
                </div>
              ) : (
                <div className="space-y-1.5">
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={form.customLocation}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          customLocation: e.target.value,
                        }))
                      }
                      className={`${inputClass} flex-1`}
                      placeholder={t(
                        "marketing.newLocationName",
                        "New location name"
                      )}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") addNewLocation();
                      }}
                    />
                    <button
                      type="button"
                      onClick={addNewLocation}
                      disabled={!form.customLocation.trim() || addingLocation}
                      className="px-3 py-2 rounded-[10px] text-sm font-semibold text-white bg-teal-600 hover:bg-teal-700 transition-colors duration-150 disabled:opacity-50"
                    >
                      {addingLocation ? (
                        <Loader2 size={14} className="animate-spin" />
                      ) : (
                        t("marketing.add", "Add")
                      )}
                    </button>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setShowCustomLocation(false);
                      setForm((prev) => ({ ...prev, customLocation: "" }));
                    }}
                    className="text-xs text-slate-500 hover:text-slate-700 underline cursor-pointer"
                  >
                    {t("marketing.cancel", "Cancel")}
                  </button>
                </div>
              )}
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
                {t("pipeline.notes", "Notes")}
              </label>
              <textarea
                value={form.notes}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, notes: e.target.value }))
                }
                rows={2}
                className={`${inputClass} resize-none`}
                placeholder={t(
                  "marketing.notesPlaceholder",
                  "Notes about this contact..."
                )}
              />
            </div>
          </div>
        </form>

        <div className="px-6 py-4 border-t border-slate-100 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-[10px] text-sm font-semibold text-slate-600 hover:bg-slate-100 transition-colors duration-150 cursor-pointer"
          >
            {t("pipeline.cancel", "Cancel")}
          </button>
          <button
            onClick={handleSubmit}
            disabled={!canSubmit || saving}
            className="px-5 py-2 rounded-[10px] text-sm font-semibold text-white bg-teal-800 hover:bg-teal-900 transition-colors duration-150 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? (
              <Loader2 size={14} className="animate-spin" />
            ) : (
              t("pipeline.save", "Save")
            )}
          </button>
        </div>
      </div>
    </div>
  );
}